import React, { useEffect, useState, useRef, useContext, useCallback, SyntheticEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import fileDownload from 'js-file-download';

// MUI
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

// MUI Icons
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

// OL
import OlFeature from 'ol/Feature';
import OlPoint from 'ol/geom/Point';
import OlSourceVector from 'ol/source/Vector';
import OlLayerVector from 'ol/layer/Vector';
import OlLineString from 'ol/geom/LineString';

// CUSTOM
import ProfileChart from '@/ui/Charts/ProfileChart';
import MapContext from '@/components/Map/MapContext';
import { GridContainer, GridItem } from '@/ui/Grid';
import GPXBuilderOL from '@/lib/gpx/gpxBuilderOL';
import { sifrarnici } from '@/lib/sifrarnici';
import { parseRouteWarnings } from '@/lib/routeWarnings';
import PaneWarnings from '@/ui/PublicPage/PaneWarnings';
import SidebarInfoBoxStaza from '../SidebarInfoBoxStaza';

import { routePointStyle } from '@/components/Map/mapStyles';

import {
  InfoPaneShareActionButton,
  InfoPaneCommentActionButton,
  InfoPaneUrlActionButton,
  InfoPaneGPXActionButton,
  InfoPaneActionButtons,
} from '@/ui/PublicPage/PublicActionButtons/';

// Types
import { MapContextType } from '@/@types/context/MapContext';
import { IInfoPane } from '@/@types/views/GIS/map';
import { DCRecord } from '@/@types/lib/dataController';

type StazeInfoProps = {
  infoPane?: IInfoPane;
  planStaza?: boolean;
  handleShowCommentPane: (newCommentPane: object) => void;
  handleOpenLDTPane: (ldtId: number, ldtStageId: number) => void;
};

const StazeInfo: FC<StazeInfoProps> = (props) => {
  const mapContext = useContext(MapContext) as MapContextType;

  const { infoPane, planStaza, handleShowCommentPane, handleOpenLDTPane } = props;

  const pointOnRouteSourceRef = useRef<OlSourceVector<OlFeature<OlPoint>>>(new OlSourceVector({}));

  const [pointOnRouteLayer, setPointOnRouteLayer] = useState<OlLayerVector<OlSourceVector<OlFeature<OlPoint>>>>(
    new OlLayerVector({
      source: pointOnRouteSourceRef.current,
      style: routePointStyle,
    })
  );

  const { t } = useTranslation();

  const hps_info_url = infoPane?.data.record?.hps_info_url as string; // will be null when non existant

  useEffect(() => {
    return () => {
      pointOnRouteSourceRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (mapContext.map) {
      pointOnRouteLayer.setMap(mapContext.map);
    }
  }, [mapContext.map]);

  const handleChartMouseEnter = (e: any, chart: any, options: any) => {
    const ind = options.dataPointIndex;
    const m = chart.data.twoDSeriesX[ind];
    const last = chart.data.twoDSeriesX[chart.data.twoDSeriesX.length - 1];
    const mrel = m / last;

    // @ts-ignore
    const geom = (infoPane?.data.record.geom as OlFeature<OlLineString>).getGeometry();
    if (geom && mrel) {
      const p = geom.getCoordinateAtM(mrel);
      const pFeatures = pointOnRouteSourceRef.current.getFeatures();
      if (Array.isArray(pFeatures)) {
        if (pFeatures.length === 0) {
          if (p) {
            pointOnRouteSourceRef.current.addFeature(
              new OlFeature<OlPoint>({
                geometry: new OlPoint(p),
              })
            );
          }
        } else {
          const pf = pFeatures[0];
          if (p) pf.setGeometry(new OlPoint(p));
        }
      }
    }
  };

  const handleChartMouseLeave = (e: any, chart: any, options: any) => {
    pointOnRouteSourceRef.current.clear();
  };

  const record = infoPane?.data.record;
  // @ts-ignore
  const feature = record?.geom as OlFeature<OlLineString>;
  const stazaLength = record?.duljina_m as number;

  const isFerata = record?.staza_tip_id === sifrarnici.staza_tipovi.ferrata ? true : false;

  const title = isFerata ? t('titles.ferata') : planStaza ? t('titles.hiking_trail') : t('titles.other_trail');

  const handleDownloadGPX = useCallback(
    (evt: SyntheticEvent) => {
      const rec = infoPane?.data.record as DCRecord;
      // @ts-ignore
      const feat = rec?.geom as OlFeature<OlLineString>;

      const gpxBuilder = new GPXBuilderOL();
      gpxBuilder.addStaza(rec, feat);
      const gpx = gpxBuilder.writeGPX();

      if (gpx) {
        const file = new Blob([gpx], { type: 'application/gpx+xml' });
        fileDownload(file, 'staza.gpx');
      }

      // }
    },
    [infoPane]
  );

  const hps_broj_staze = record?.hps_broj_staze ? (record.hps_broj_staze as string) : '';
  const hps_drustvo = record?.hps_drustvo;
  const hps_drustvo_label = hps_drustvo ? ` - ${hps_drustvo}` : '';
  const stazaWarnings = parseRouteWarnings(record).warnings;

  return (
    <Box height="100%" width="100%">
      <Box p={2}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          {record?.hps_naziv ? <Typography variant="h6">{record?.hps_naziv as string}</Typography> : null}
          {record?.hps_broj_staze ? <Typography variant="body1">{`(${hps_broj_staze}${hps_drustvo_label})`}</Typography> : null}
          {infoPane?.data.chips && infoPane?.data.chips?.length > 0
            ? infoPane?.data.chips?.map((ldtChip) => (
                <Chip
                  label={ldtChip.displayName}
                  color="primary"
                  onClick={() => {
                    handleOpenLDTPane ? handleOpenLDTPane(ldtChip.id, ldtChip.initialIdToSelect as number) : () => {};
                  }}
                  style={{
                    marginTop: 5,
                    marginLeft: 2,
                    color: '#DDFFD7',
                  }}
                />
              ))
            : null}
        </Box>

        <Box mt={2}>
          {record !== undefined ? (
            <GridContainer spacing={1}>
              <GridItem size={3}>
                <Stack>
                  <SettingsEthernetIcon fontSize="medium" color="primary" />
                  <Typography color="primary" variant="h6" sx={{ py: '2px' }}>{`${record.duljina_m as number} m`}</Typography>
                  <Typography variant="caption">{t('staze.duljina') as string}</Typography>
                </Stack>
              </GridItem>
              <GridItem size={3}>
                <Stack>
                  <AccessTimeIcon fontSize="medium" color="primary" />
                  <Typography color="primary" variant="h6" sx={{ py: '2px' }}>
                    {record.vrijeme_hhmm as string}
                  </Typography>
                  <Typography variant="caption">{t('route.trajanje') as string}</Typography>
                </Stack>
              </GridItem>
              <GridItem size={3}>
                <Stack>
                  <OpenInFullIcon fontSize="medium" color="primary" />
                  <Typography color="primary" variant="h6" sx={{ py: '2px' }}>{`${record.delta_h_m as number} m`}</Typography>
                  <Typography variant="caption">{t('staze.delta_h') as string}</Typography>
                </Stack>
              </GridItem>
              <GridItem size={3}>{/* empty */}</GridItem>
            </GridContainer>
          ) : (
            <></>
          )}
        </Box>
      </Box>

      {record !== undefined && feature !== undefined ? (
        <ProfileChart
          // @ts-ignore
          series={[
            {
              name: 'Staza',
              data: feature
                ?.getGeometry()
                ?.getCoordinates()
                .map((c: number[]) => [Math.round(c[3] * stazaLength), Math.round(c[2])]) as number[][],
            },
          ]}
          onChartMouseEnter={handleChartMouseEnter}
          onChartMouseLeave={handleChartMouseLeave}
        />
      ) : null}

      <Box sx={{ px: 2 }} mb={2}>
        <InfoPaneActionButtons>
          <InfoPaneShareActionButton infoPane={infoPane} object_type="staza" />
          <InfoPaneCommentActionButton
            objekt_type="staze"
            handleGoToComments={handleShowCommentPane}
            // @ts-ignore
            data={infoPane?.data}
          />
          <InfoPaneGPXActionButton onClick={handleDownloadGPX} />
          {hps_info_url !== null ? <InfoPaneUrlActionButton url={hps_info_url} /> : null}
        </InfoPaneActionButtons>
      </Box>

      <Divider variant="middle" sx={{ pt: 2 }} />

      {stazaWarnings.length > 0 ? <PaneWarnings warnings={stazaWarnings} /> : null}

      <SidebarInfoBoxStaza />
    </Box>
  );
};

export default StazeInfo;
